// @ts-strict-ignore
import { setProperty, deleteProperty, getProperty } from 'dot-prop';
import { v4 as uuidv4 } from 'uuid';
import {
  getNumericPathIndices, getActionName, setActionName, computeNextActionName,
  isActionDataObjectEphemeral,
} from 'flow/service/actions';
import { createAction } from '../../engine/create/actions/actions';
import type { CreateActionPayload } from '../../engine/create/actions/types';
import {
  modifyRelevantDataFromActionRemoval,
  modifyRelevantDataFromActionUpdate,
  modifyRelevantDataFromSnippetRemoval,
} from '../../engine/update/actions';
import {
  snapshotAndCloneWorkflowData,
  compileWorkflowDataDependencies,
  getParentPathAndExpressionItem,
  resetPendingGroup,
} from '../helpers';
import {
  groupUniqueActionNamesByType,
  isActionOfType,
} from 'flow/service/actions/actions';
import type { WorkflowState } from '../state';
import { WorkflowEngineException } from '../../engine/schema';
import type {
  Action,
  DataRoute,
  Expression,
  ExpressionTerm,
  SchemaInstance,
  Threshold,
  PendingParenthesisInfo,
} from '../../engine/types';
import {
  assignDefaultsToDataRoute, insertIntoExpression, withFunctionSeparators,
} from '../../engine/update/expression';
import {
  OperationMessageType,
} from '../../engine/types';
import { correctAmbiguousBooleanRule } from '../../engine/update/rules/rules';
import { ReduxErrorTypes } from '../constants';
import {
  getExpressionDataRouteIds,
  getExpressionFunction,
  getIsExpressionFunctionString,
  settleFunctions,
} from '../../engine/utils';
import { RuleUpdateOperationType } from '../../engine/update/constants';
import { getGroupStartIndexOffset } from 'flow/editor/components/useCaretControl/helpers';
import { ACTION_TYPES_ALLOWING_NAME_COLLISIONS } from 'flow/service/actions/constants';
import { pruneActionFromProps } from 'flow/store/engine/update/props';
import { ActionType } from 'flow/store/engine/constants';


// ================ Action Reducers ================

/**
 * Reducer to add an Action with optional Thresholds and Data Routes.
 * Performs minimal validation on the action.
 */
export function addAction(
  state: WorkflowState,
  action: {
    payload: {
      action: Action;
      thresholds?: Threshold[];
      dataRoutes?: DataRoute[];
    };
  },
) {
  const currentActions = state.workflowData.actions;
  const { thresholds, dataRoutes } = action.payload;

  let actionToAdd = structuredClone(action.payload.action);
  const nextActionName = computeNextActionName(actionToAdd, currentActions);
  if (getActionName(actionToAdd) !== nextActionName) {
    actionToAdd = setActionName(actionToAdd, nextActionName);
  }


  state.workflowData.actions = setProperty(currentActions, actionToAdd.publicId, actionToAdd);
  thresholds?.forEach(threshold => {
    // defend against thresholds not belonging to the action
    if (threshold.actionId !== actionToAdd.publicId) {
      return;
    }

    // Skip thresholds with matrix model RHS actions when cloning
    // Matrix models cannot be used as right-hand side actions in dynamic comparisons
    if (threshold.right.actionId) {
      const rightAction = state.workflowData.actions[threshold.right.actionId];
      if (rightAction && isActionOfType(rightAction, [
        ActionType.MATRIX_MODEL, ActionType.IMPORTED_MATRIX_MODEL,
      ])) {
        return;
      }
    }

    state.workflowData.thresholds = setProperty(
      state.workflowData.thresholds,
      threshold.publicId,
      threshold);
  });

  dataRoutes?.forEach(dataRoute => {
    // defend against dataRoute not belonging to the action
    if (dataRoute.targetId !== actionToAdd.publicId) {
      return;
    }

    state.workflowData.dataRoutes = setProperty(
      state.workflowData.dataRoutes,
      dataRoute.publicId,
      dataRoute,
    );
  });
}


/**
 * Reducer to create workflow Actions from a payload.
 * This function ensures that no duplicate actions are created, and supports
 * any type of action
 */
export function createActions(
  state: WorkflowState,
  action: { payload: CreateActionPayload | CreateActionPayload[] },
) {
  const {
    payload,
  } = action;

  const {
    workflowData: { actions: existingActions }, workflowSnapshots,
  } = snapshotAndCloneWorkflowData(state);

  const uniqueActionNamesByType = groupUniqueActionNamesByType(
    existingActions,
  );

  let updatedActions = existingActions;
  const createActionPayloads = Array.isArray(payload) ? payload : [payload];

  createActionPayloads
    .forEach(createActionPayload => {
      const newAction = createAction(createActionPayload);
      const name = getActionName(newAction);
      const { publicId: path, type } = newAction;

      // Prevents creating duplicate actions
      if (!ACTION_TYPES_ALLOWING_NAME_COLLISIONS.includes(type)) {
        // early return if the action being added already exists (compared using the action's name)
        if (uniqueActionNamesByType.get(type)?.has(name)) {
          return;
        }

        // add a new Set for the action type if this is the first action of that type being added
        if (!uniqueActionNamesByType.has(type)) {
          uniqueActionNamesByType.set(type, new Set());
        }

        uniqueActionNamesByType.get(type).add(name);
      }

      updatedActions = setProperty(existingActions, path, newAction);
    });

  state.workflowData.actions = updatedActions;
  state.workflowSnapshots = workflowSnapshots;
}


export function removeAction(
  state: WorkflowState,
  action: {
    payload: { id: string };
  },
) {
  const { workflowData, workflowSnapshots } = snapshotAndCloneWorkflowData(state);
  const { id } = action.payload;
  const originalAction = workflowData.actions[id];
  if (!originalAction) return;
  deleteProperty(workflowData.actions, id);
  if (isActionDataObjectEphemeral(originalAction)) {
    state.workflowProps = pruneActionFromProps({
      action: originalAction,
      workflowProps: state.workflowProps,
      workflowActions: workflowData.actions,
    });
  }
  if (isActionOfType(originalAction, ActionType.SNIPPET)) {
    modifyRelevantDataFromSnippetRemoval({ snippetAction: originalAction, workflowData });
  } else {
    modifyRelevantDataFromActionRemoval({ originalTargetValue: originalAction, workflowData });
  }
  state.workflowData = compileWorkflowDataDependencies(workflowData);
  state.workflowSnapshots = workflowSnapshots;
}

export function updateAction(
  state: WorkflowState,
  action: {
    payload: {
      path: string;
      value: any;
      skipSnapshotAndDependencies?: boolean;
    };
  },
) {
  const { path, value, skipSnapshotAndDependencies = false } = action.payload;

  if (skipSnapshotAndDependencies) {
    state.workflowData.actions = setProperty(state.workflowData.actions, path, value);
  } else {
    const { workflowData, workflowSnapshots } = snapshotAndCloneWorkflowData(state);
    modifyRelevantDataFromActionUpdate({ path, value, workflowData });
    workflowData.actions = setProperty(workflowData.actions, path, value);
    state.workflowData = compileWorkflowDataDependencies(workflowData);
    state.workflowSnapshots = workflowSnapshots;
  }
}

export function clearActionRulesOperatorAmbiguousMode(
  state: WorkflowState,
  action: {
    payload: {
      id: string;
      newRuleOperatorForCorrection: 'and' | 'or';
      skipSnapshotAndDependencies?: boolean;
    };
  },
) {
  const {
    id,
    newRuleOperatorForCorrection,
    skipSnapshotAndDependencies = false,
  } = action.payload;

  const { workflowData, workflowSnapshots } = skipSnapshotAndDependencies
    ? { workflowData: state.workflowData, workflowSnapshots: state.workflowSnapshots }
    : snapshotAndCloneWorkflowData(state);

  const rules = getProperty(workflowData.actions, `${id}.rules`);
  const cleanedRules = correctAmbiguousBooleanRule({
    newRuleOperatorForCorrection,
    originalRule: rules,
  });
  workflowData.actions = setProperty(workflowData.actions, `${id}.rules`, cleanedRules);
  state.workflowData = compileWorkflowDataDependencies(workflowData);
  state.workflowSnapshots = workflowSnapshots;

  state.workflowOperationDescriptions.push({
    id: uuidv4(),
    publicId: id,
    type: OperationMessageType.actionRuleThreshold,
    timestamp: Date.now(),
    message: RuleUpdateOperationType.AUTO_CORRECTED_AMBIGUOUS_OPERATOR_RULE,
    data: undefined,
  });
}

export function createActionOutputExpressionItem(
  state: WorkflowState,
  action: {
    payload: {
      id: string;
      path: string;
      value: any;
      insertion?: boolean;
    };
  },
) {
  const { workflowData, workflowSnapshots } = snapshotAndCloneWorkflowData(state);
  const {
    id, path, value, insertion,
  } = action.payload;
  const existingExpression: Expression = getProperty(workflowData.actions, `${id}.output.expression`);

  if (!existingExpression) {
    throw new WorkflowEngineException('', ReduxErrorTypes.OUTPUT_EXPRESSION_NOT_FOUND);
  }

  const { expression: updatedExpression, wentInsideAwaitingGroup } = insertIntoExpression({
    expression: existingExpression,
    path,
    value,
    insertion,
  });

  workflowData.actions = setProperty(
    workflowData.actions, `${id}.output.expression`, updatedExpression);

  // a first value, one inside a group, and one replacing an operand all leave a pending parenthesis alone
  const keepsPendingGroup = wentInsideAwaitingGroup
    || (!insertion && Boolean(path))
    || existingExpression.length === 0;

  if (!keepsPendingGroup) {
    resetPendingGroup(state);
  }

  state.workflowData = compileWorkflowDataDependencies(workflowData);
  state.workflowSnapshots = workflowSnapshots;
}

export function updateActionOutputExpressionItem(
  state: WorkflowState,
  action: {
    payload: {
      id: string;
      expressionPath: string;
      value: any;
    };
  },
) {
  const { workflowData, workflowSnapshots } = snapshotAndCloneWorkflowData(state);
  const { expressionPath, id, value } = action.payload;
  const existingExpression: Expression = getProperty(workflowData.actions, `${id}.output.expression`);

  if (!existingExpression) {
    throw new WorkflowEngineException('', ReduxErrorTypes.INVALID_ACTION_ID);
  }

  workflowData.actions = setProperty(workflowData.actions, `${id}.output.expression${expressionPath}`, value);
  state.workflowData = compileWorkflowDataDependencies(workflowData);
  state.workflowSnapshots = workflowSnapshots;
}

/**
 * Switches every min/max in the group, because a group mixing both has no clear meaning. Takes either
 * the group's own path or the path of an operator inside it.
 */
export function updateActionOutputExpressionFunction(
  state: WorkflowState,
  action: {
    payload: {
      id: string;
      expressionPath: string;
      value: string;
    };
  },
) {
  const { workflowData, workflowSnapshots } = snapshotAndCloneWorkflowData(state);
  const { expressionPath, id, value } = action.payload;
  const existingExpression: Expression = getProperty(workflowData.actions, `${id}.output.expression`);

  if (!existingExpression) {
    throw new WorkflowEngineException('', ReduxErrorTypes.INVALID_ACTION_ID);
  }

  const indices = getNumericPathIndices(expressionPath);
  const {
    pathWithoutLastIndex, parentExpressionArrayOfTheExpressionOnTheLastIndex,
  } = getParentPathAndExpressionItem({ indices, existingExpression });

  const target = getProperty(existingExpression, expressionPath);

  // a single index means the operator is at the top level, so pathWithoutLastIndex is ''
  const enclosingGroup = indices.length === 1
    ? existingExpression
    : parentExpressionArrayOfTheExpressionOnTheLastIndex;

  const [group, groupPath] = Array.isArray(target)
    ? [target, expressionPath]
    : [enclosingGroup, pathWithoutLastIndex];

  const updatedGroup = group.map(item => (
    typeof item === 'string' && getIsExpressionFunctionString(item) ? value : item
  ));

  workflowData.actions = setProperty(
    workflowData.actions,
    `${id}.output.expression${groupPath}`,
    updatedGroup,
  );
  state.workflowData = compileWorkflowDataDependencies(workflowData);
  state.workflowSnapshots = workflowSnapshots;
}

/**
 * Adds the trailing min/max back, which draws the comma inviting another argument. Used to reopen a
 * group the caret had already left.
 */
export function addActionOutputExpressionTrailingOperator(
  state: WorkflowState,
  action: {
    payload: {
      id: string;
      expressionPath: string;
    };
  },
) {
  const { workflowData, workflowSnapshots } = snapshotAndCloneWorkflowData(state);
  const { expressionPath, id } = action.payload;
  const existingExpression: Expression = getProperty(workflowData.actions, `${id}.output.expression`);

  if (!existingExpression) {
    throw new WorkflowEngineException('', ReduxErrorTypes.INVALID_ACTION_ID);
  }

  const group = getProperty(existingExpression, expressionPath);
  const groupFunction = getExpressionFunction(group);

  if (!groupFunction) return;

  workflowData.actions = setProperty(
    workflowData.actions,
    `${id}.output.expression${expressionPath}`,
    withFunctionSeparators(group, groupFunction),
  );
  state.workflowData = compileWorkflowDataDependencies(workflowData);
  state.workflowSnapshots = workflowSnapshots;
}

/**
 * Puts the operand at expressionPath into a new group along with the operator, so the operator applies
 * to that argument only: Min ( a , b ) becomes Min ( a , ( b * ) ).
 */
export function addActionOutputExpressionOperatorToOperand(
  state: WorkflowState,
  action: {
    payload: {
      id: string;
      expressionPath: string;
      value: string;
    };
  },
) {
  const { workflowData, workflowSnapshots } = snapshotAndCloneWorkflowData(state);
  const { expressionPath, id, value } = action.payload;
  const existingExpression: Expression = getProperty(workflowData.actions, `${id}.output.expression`);

  if (!existingExpression) {
    throw new WorkflowEngineException('', ReduxErrorTypes.OUTPUT_EXPRESSION_NOT_FOUND);
  }

  const operand = getProperty(existingExpression, expressionPath);

  if (operand === undefined) return;

  workflowData.actions = setProperty(
    workflowData.actions,
    `${id}.output.expression${expressionPath}`,
    [operand, value],
  );
  state.workflowData = compileWorkflowDataDependencies(workflowData);
  state.workflowSnapshots = workflowSnapshots;
}

/**
 * Closes every min/max group except the one at keepOpenPath, by removing the trailing operator that
 * draws its comma. Pass no path to close all of them.
 */
export function settleActionOutputExpressionFunctions(
  state: WorkflowState,
  action: {
    payload: {
      id: string;
      keepOpenPath?: string;
    };
  },
) {
  const { workflowData, workflowSnapshots } = snapshotAndCloneWorkflowData(state);
  const { keepOpenPath, id } = action.payload;
  const existingExpression: Expression = getProperty(workflowData.actions, `${id}.output.expression`);

  // The editor also dispatches this as cleanup when it closes, and the action can already be
  // removed by then. A missing action is normal here, unlike in the reducers a user's edit drives.
  if (!existingExpression) return;

  workflowData.actions = setProperty(
    workflowData.actions,
    `${id}.output.expression`,
    settleFunctions(existingExpression, { keepOpen: keepOpenPath }),
  );
  state.workflowData = compileWorkflowDataDependencies(workflowData);
  state.workflowSnapshots = workflowSnapshots;
}

/**
 * Deletes the data routes the replaced item used, which updateActionOutputExpressionItem does not do.
 * The item may be a whole group or a single term.
 */
export function replaceActionOutputExpressionItem(
  state: WorkflowState,
  action: {
    payload: {
      id: string;
      expressionPath: string;
      value?: ExpressionTerm | ExpressionTerm[];
      newDataRoute?: DataRoute;
    };
  },
) {
  const { workflowData, workflowSnapshots } = snapshotAndCloneWorkflowData(state);
  const { workflowProps } = state;
  const {
    expressionPath, id, value, newDataRoute,
  } = action.payload;
  const existingExpression: Expression = getProperty(workflowData.actions, `${id}.output.expression`);

  if (!existingExpression) {
    throw new WorkflowEngineException('', ReduxErrorTypes.INVALID_ACTION_ID);
  }

  const replacedItem = getProperty(existingExpression, expressionPath);
  const replacedTerms = Array.isArray(replacedItem) ? replacedItem : [replacedItem];
  getExpressionDataRouteIds(replacedTerms).forEach(dataRouteId => {
    deleteProperty(workflowData.dataRoutes, dataRouteId);
  });

  let replacement = value;
  if (newDataRoute) {
    // assignDefaultsToDataRoute reads the source action's type, so a missing one would throw there
    if (!workflowData.actions[newDataRoute.sourceId]) {
      throw new WorkflowEngineException('', ReduxErrorTypes.INVALID_ACTION_ID);
    }

    workflowData.dataRoutes[newDataRoute.publicId] = assignDefaultsToDataRoute({
      path: '',
      dataRoute: newDataRoute,
      workflowData: workflowData as SchemaInstance,
      workflowProps,
      value: newDataRoute.sourceId,
    });
    replacement = { data_route_id: newDataRoute.publicId };
  }

  workflowData.actions = setProperty(
    workflowData.actions,
    `${id}.output.expression${expressionPath}`,
    replacement,
  );
  state.workflowData = compileWorkflowDataDependencies(workflowData);
  state.workflowSnapshots = workflowSnapshots;
}

export function removeActionOutputExpressionItem(
  state: WorkflowState,
  action: {
    payload: {
      id: string;
      expressionPath: string;
    };
  },
) {
  const { workflowData, workflowSnapshots } = snapshotAndCloneWorkflowData(state);
  const { expressionPath, id } = action.payload;
  const existingExpression: Expression = getProperty(workflowData.actions, `${id}.output.expression`);

  if (!existingExpression) {
    throw new WorkflowEngineException('', ReduxErrorTypes.INVALID_ACTION_ID);
  }

  // removing a group also removes the data routes its arguments used
  const removedItem = getProperty(existingExpression, expressionPath);
  const removedTerms = Array.isArray(removedItem) ? removedItem : [removedItem];
  getExpressionDataRouteIds(removedTerms).forEach(dataRouteId => {
    deleteProperty(workflowData.dataRoutes, dataRouteId);
  });

  const indices = getNumericPathIndices(expressionPath); // grab only digits from the path string
  if (indices.length === 1) {
    existingExpression.splice(Number(indices[0]), 1);
  } else {
    const {
      pathWithoutLastIndex, parentExpressionArrayOfTheExpressionOnTheLastIndex,
    } = getParentPathAndExpressionItem({
      indices,
      existingExpression,
    });
    parentExpressionArrayOfTheExpressionOnTheLastIndex.splice(
      Number(indices.at(-1)), 1);
    setProperty(
      existingExpression,
      pathWithoutLastIndex,
      parentExpressionArrayOfTheExpressionOnTheLastIndex,
    );
  }

  resetPendingGroup(state);

  workflowData.actions = setProperty(workflowData.actions, `${id}.output.expression`, existingExpression);
  state.workflowData = compileWorkflowDataDependencies(workflowData);
  state.workflowSnapshots = workflowSnapshots;
}

export function groupActionOutputExpressionItem(
  state: WorkflowState,
  action: {
    payload: {
      id: string;
      groupOpeningInfo: PendingParenthesisInfo;
      groupClosingPath: string;
    };
  },
) {
  const { workflowData, workflowSnapshots } = snapshotAndCloneWorkflowData(state);
  const { groupOpeningInfo, groupClosingPath, id } = action.payload;

  let existingExpression: Expression = getProperty(workflowData.actions, `${id}.output.expression`);

  if (!existingExpression) {
    throw new WorkflowEngineException('', ReduxErrorTypes.INVALID_ACTION_ID);
  }

  const { path, side } = groupOpeningInfo;
  // grab only digits from the path string
  const openingPathIndices = getNumericPathIndices(path);
  const closingPathIndices = getNumericPathIndices(groupClosingPath);

  // When we set a paranthesis to be before an expression item,
  // we want to ensure that we don't remove the expression item it precedes
  const groupOpenIndexOffset = getGroupStartIndexOffset(side);
  const openingGroupIndex = Number(openingPathIndices.at(-1)) + groupOpenIndexOffset;
  const closingGroupIndex = Number(closingPathIndices.at(-1)) + 1;

  if (openingPathIndices.length === 1) {
    // expression is flat 1-dimen array
    const rangeOfItemsFromPath = existingExpression.slice(
      openingGroupIndex, closingGroupIndex,
    ) as ExpressionTerm[];

    // spread what was in the group into the existing expression, at the index where the group was located at
    existingExpression = [
      ...existingExpression.slice(0, openingGroupIndex),
      rangeOfItemsFromPath,
      ...existingExpression.slice(closingGroupIndex) || [],
    ];
  } else {
    const {
      pathWithoutLastIndex,
      parentExpressionArrayOfTheExpressionOnTheLastIndex: parentExpressionArray,
    } = getParentPathAndExpressionItem({
      indices: openingPathIndices,
      existingExpression,
    });

    if (parentExpressionArray) {
      // remove and retrieve "cut" the items in the range of group opening path / closing path
      const rangeOfItemsFromPath = parentExpressionArray.slice(
        openingGroupIndex, closingGroupIndex,
      );

      // spread what was in the group into the existing expression, at the index where the group was located at
      const updatedParentExpressionArray = [
        ...parentExpressionArray.slice(0, openingGroupIndex),
        rangeOfItemsFromPath,
        ...parentExpressionArray.slice(closingGroupIndex) || [],
      ];

      setProperty(
        existingExpression,
        pathWithoutLastIndex,
        updatedParentExpressionArray,
      );
    }
  }

  resetPendingGroup(state);

  workflowData.actions = setProperty(workflowData.actions, `${id}.output.expression`, existingExpression);
  state.workflowData = compileWorkflowDataDependencies(workflowData);
  state.workflowSnapshots = workflowSnapshots;
}

export function ungroupActionOutputExpressionItem(
  state: WorkflowState,
  action: {
    payload: {
      id: string;
      expressionPath: string;
    };
  },
) {
  const { workflowData, workflowSnapshots } = snapshotAndCloneWorkflowData(state);
  const { expressionPath, id } = action.payload;
  let existingExpression: Expression = getProperty(workflowData.actions, `${id}.output.expression`);

  if (!existingExpression) {
    throw new WorkflowEngineException('', ReduxErrorTypes.INVALID_ACTION_ID);
  }

  const indices = getNumericPathIndices(expressionPath); // grab only digits from the path string
  if (indices.length === 1) {
    // expression is flat 1-dimen array
    // we basically spread what was in the group into the existing expression, at the index where the group was located at
    existingExpression = [
      ...existingExpression.slice(0, Number(indices.at(-1))),
      ...existingExpression[Number(indices.at(-1))] as ExpressionTerm[],
      ...existingExpression.slice(
        Number(indices.at(-1)) + 1) || [],
    ];
  } else {
    const {
      pathWithoutLastIndex, parentExpressionArrayOfTheExpressionOnTheLastIndex,
    } = getParentPathAndExpressionItem({
      indices,
      existingExpression,
    });

    // spread what was in the group into the existing expression, at the index where the group was located at
    const updatedParentExpressionArray = [
      ...parentExpressionArrayOfTheExpressionOnTheLastIndex.slice(0, Number(indices.at(-1))),
      ...parentExpressionArrayOfTheExpressionOnTheLastIndex[
        Number(indices.at(-1))] as ExpressionTerm[],
      ...parentExpressionArrayOfTheExpressionOnTheLastIndex.slice(
        Number(indices.at(-1)) + 1) || [],
    ];

    setProperty(
      existingExpression,
      pathWithoutLastIndex,
      updatedParentExpressionArray,
    );
  }

  resetPendingGroup(state);

  workflowData.actions = setProperty(workflowData.actions, `${id}.output.expression`, existingExpression);
  state.workflowData = compileWorkflowDataDependencies(workflowData);
  state.workflowSnapshots = workflowSnapshots;
}
